// src/pages/LegalPage.jsx
import { Link, useParams } from 'react-router-dom';
import Layout, { useLanguage } from '../components/Layout';

export default function LegalPage() {
  const { t } = useLanguage();
  const { type } = useParams();
  
  const legalContent = {
    privacy: {
      title: t('legal.privacy.title') || 'Privacy Policy',
      content: (
        <>
          <h3>{t('legal.privacy.section1') || 'Information We Collect'}</h3>
          <p>
            {t('legal.privacy.section1Text') || 'Nexcom collects information to provide better services to all our customers. This includes personal information such as name, email address, phone number, and physical address.'}
          </p>
          
          <h3>{t('legal.privacy.section2') || 'How We Use Information'}</h3>
          <p>
            {t('legal.privacy.section2Text') || 'We use the information we collect to:'}
          </p>
          <ul>
            <li>{t('legal.privacy.list1') || 'Provide, maintain, and improve our services'}</li>
            <li>{t('legal.privacy.list2') || 'Process your orders and payments'}</li>
            <li>{t('legal.privacy.list3') || 'Send you technical notices and support messages'}</li>
            <li>{t('legal.privacy.list4') || 'Communicate with you about products and services'}</li>
            <li>{t('legal.privacy.list5') || 'Comply with legal obligations'}</li>
          </ul>
          
          <h3>{t('legal.privacy.section3') || 'Data Security'}</h3>
          <p>
            {t('legal.privacy.section3Text') || 'We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction.'}
          </p>
        </>
      )
    },
    terms: {
      title: t('legal.terms.title') || 'Terms & Conditions',
      content: (
        <>
          <h3>{t('legal.terms.section1') || 'Acceptance of Terms'}</h3>
          <p>
            {t('legal.terms.section1Text') || 'By using Nexcom services, you agree to these Terms and Conditions. If you do not agree, please do not use our services.'}
          </p>
          
          <h3>{t('legal.terms.section2') || 'Service Description'}</h3>
          <p>
            {t('legal.terms.section2Text') || 'Nexcom provides fixed wireless internet services to residential and business customers in Kisumu, Kenya. Services are subject to availability and coverage area.'}
          </p>
          
          <h3>{t('legal.terms.section3') || 'Payment and Billing'}</h3>
          <ul>
            <li>{t('legal.terms.list1') || 'All payments are due on the 1st of each month'}</li>
            <li>{t('legal.terms.list2') || 'Late payments may result in service suspension'}</li>
            <li>{t('legal.terms.list3') || 'Installation fees are non-refundable'}</li>
            <li>{t('legal.terms.list4') || 'Prices are subject to change with 30 days notice'}</li>
          </ul>
        </>
      )
    },
    cookies: {
      title: t('legal.cookies.title') || 'Cookie Policy',
      content: (
        <>
          <h3>{t('legal.cookies.section1') || 'What Are Cookies'}</h3>
          <p>
            {t('legal.cookies.section1Text') || 'Cookies are small text files stored on your device when you visit our website. They help us provide you with a better experience.'}
          </p>
          
          <h3>{t('legal.cookies.section2') || 'How We Use Cookies'}</h3>
          <p>
            {t('legal.cookies.section2Text') || 'We use cookies to:'}
          </p>
          <ul>
            <li>{t('legal.cookies.list1') || 'Remember your preferences'}</li>
            <li>{t('legal.cookies.list2') || 'Analyze how you use our website'}</li>
            <li>{t('legal.cookies.list3') || 'Personalize content and ads'}</li>
            <li>{t('legal.cookies.list4') || 'Improve our services'}</li>
          </ul>
        </>
      )
    },
    service: {
      title: t('legal.service.title') || 'Service Agreement',
      content: (
        <>
          <h3>{t('legal.service.section1') || 'Service Level Agreement'}</h3>
          <p>
            {t('legal.service.section1Text') || 'Nexcom commits to providing reliable internet services with the following service levels:'}
          </p>
          <ul>
            <li>{t('legal.service.list1') || '99.9% network uptime guarantee'}</li>
            <li>{t('legal.service.list2') || '4-hour response time for business customers'}</li>
            <li>{t('legal.service.list3') || '24-hour resolution time for residential customers'}</li>
            <li>{t('legal.service.list4') || '24/7 technical support availability'}</li>
          </ul>
        </>
      )
    },
    dataprotection: {
      title: t('legal.dataprotection.title') || 'Data Protection Policy',
      content: (
        <>
          <h3>{t('legal.dataprotection.section1') || 'Our Commitment to Data Protection'}</h3>
          <p>
            {t('legal.dataprotection.section1Text') || 'At Nexcom, we are committed to protecting the privacy and security of your personal data. This Data Protection Policy explains how we collect, use, store, and protect your personal information in compliance with the Kenya Data Protection Act, 2019 and other applicable data protection laws.'}
          </p>

          <h3>{t('legal.dataprotection.section2') || 'Data Protection Principles'}</h3>
          <p>
            {t('legal.dataprotection.section2Text') || 'We adhere to the following data protection principles:'}
          </p>
          <ul>
            <li><strong>{t('legal.dataprotection.principle1') || 'Lawfulness, Fairness, and Transparency:'}</strong> {t('legal.dataprotection.principle1Text') || 'We process personal data lawfully, fairly, and in a transparent manner.'}</li>
            <li><strong>{t('legal.dataprotection.principle2') || 'Purpose Limitation:'}</strong> {t('legal.dataprotection.principle2Text') || 'We collect personal data for specified, explicit, and legitimate purposes.'}</li>
            <li><strong>{t('legal.dataprotection.principle3') || 'Data Minimization:'}</strong> {t('legal.dataprotection.principle3Text') || 'We collect only the personal data that is necessary for the intended purpose.'}</li>
            <li><strong>{t('legal.dataprotection.principle4') || 'Accuracy:'}</strong> {t('legal.dataprotection.principle4Text') || 'We keep personal data accurate and up to date.'}</li>
            <li><strong>{t('legal.dataprotection.principle5') || 'Storage Limitation:'}</strong> {t('legal.dataprotection.principle5Text') || 'We retain personal data only for as long as necessary.'}</li>
            <li><strong>{t('legal.dataprotection.principle6') || 'Integrity and Confidentiality:'}</strong> {t('legal.dataprotection.principle6Text') || 'We process personal data securely, protecting against unauthorized or unlawful processing.'}</li>
            <li><strong>{t('legal.dataprotection.principle7') || 'Accountability:'}</strong> {t('legal.dataprotection.principle7Text') || 'We take responsibility for complying with data protection principles.'}</li>
          </ul>

          <h3>{t('legal.dataprotection.section3') || 'Your Data Protection Rights'}</h3>
          <p>
            {t('legal.dataprotection.section3Text') || 'Under the Kenya Data Protection Act, 2019, you have the following rights:'}
          </p>
          <ul>
            <li><strong>{t('legal.dataprotection.right1') || 'Right to Access:'}</strong> {t('legal.dataprotection.right1Text') || 'You have the right to request access to your personal data.'}</li>
            <li><strong>{t('legal.dataprotection.right2') || 'Right to Rectification:'}</strong> {t('legal.dataprotection.right2Text') || 'You have the right to request correction of inaccurate personal data.'}</li>
            <li><strong>{t('legal.dataprotection.right3') || 'Right to Erasure:'}</strong> {t('legal.dataprotection.right3Text') || 'You have the right to request deletion of your personal data under certain circumstances.'}</li>
            <li><strong>{t('legal.dataprotection.right4') || 'Right to Restrict Processing:'}</strong> {t('legal.dataprotection.right4Text') || 'You have the right to request restriction of processing of your personal data.'}</li>
            <li><strong>{t('legal.dataprotection.right5') || 'Right to Data Portability:'}</strong> {t('legal.dataprotection.right5Text') || 'You have the right to receive your personal data in a structured, commonly used format.'}</li>
            <li><strong>{t('legal.dataprotection.right6') || 'Right to Object:'}</strong> {t('legal.dataprotection.right6Text') || 'You have the right to object to processing of your personal data.'}</li>
            <li><strong>{t('legal.dataprotection.right7') || 'Right to Lodge a Complaint:'}</strong> {t('legal.dataprotection.right7Text') || 'You have the right to lodge a complaint with the Office of the Data Protection Commissioner (ODPC).'}</li>
          </ul>

          <h3>{t('legal.dataprotection.section4') || 'How We Collect Your Data'}</h3>
          <p>
            {t('legal.dataprotection.section4Text') || 'We collect personal data through various channels including:'}
          </p>
          <ul>
            <li>{t('legal.dataprotection.collect1') || 'Online forms and applications'}</li>
            <li>{t('legal.dataprotection.collect2') || 'Telephone calls and SMS communications'}</li>
            <li>{t('legal.dataprotection.collect3') || 'Email correspondence'}</li>
            <li>{t('legal.dataprotection.collect4') || 'Service installations and on-site visits'}</li>
            <li>{t('legal.dataprotection.collect5') || 'Customer support interactions'}</li>
          </ul>

          <h3>{t('legal.dataprotection.section5') || 'Types of Data We Collect'}</h3>
          <p>
            {t('legal.dataprotection.section5Text') || 'We collect the following categories of personal data:'}
          </p>
          <ul>
            <li><strong>{t('legal.dataprotection.type1') || 'Identity Data:'}</strong> {t('legal.dataprotection.type1Text') || 'Name, ID number, date of birth, gender'}</li>
            <li><strong>{t('legal.dataprotection.type2') || 'Contact Data:'}</strong> {t('legal.dataprotection.type2Text') || 'Address, email address, phone numbers'}</li>
            <li><strong>{t('legal.dataprotection.type3') || 'Financial Data:'}</strong> {t('legal.dataprotection.type3Text') || 'Payment details, billing information'}</li>
            <li><strong>{t('legal.dataprotection.type4') || 'Technical Data:'}</strong> {t('legal.dataprotection.type4Text') || 'IP address, device information, usage statistics'}</li>
            <li><strong>{t('legal.dataprotection.type5') || 'Service Data:'}</strong> {t('legal.dataprotection.type5Text') || 'Service preferences, account history, support tickets'}</li>
          </ul>

          <h3>{t('legal.dataprotection.section6') || 'How We Use Your Data'}</h3>
          <p>
            {t('legal.dataprotection.section6Text') || 'We use your personal data for the following purposes:'}
          </p>
          <ul>
            <li>{t('legal.dataprotection.use1') || 'To provide and maintain our internet services'}</li>
            <li>{t('legal.dataprotection.use2') || 'To process payments and manage billing'}</li>
            <li>{t('legal.dataprotection.use3') || 'To provide customer support and respond to inquiries'}</li>
            <li>{t('legal.dataprotection.use4') || 'To send service updates and technical notices'}</li>
            <li>{t('legal.dataprotection.use5') || 'To comply with legal and regulatory requirements'}</li>
            <li>{t('legal.dataprotection.use6') || 'To improve our services and customer experience'}</li>
            <li>{t('legal.dataprotection.use7') || 'To prevent fraud and ensure network security'}</li>
          </ul>

          <h3>{t('legal.dataprotection.section7') || 'Data Security Measures'}</h3>
          <p>
            {t('legal.dataprotection.section7Text') || 'We implement robust security measures to protect your personal data:'}
          </p>
          <ul>
            <li>{t('legal.dataprotection.security1') || 'Encryption of data in transit and at rest'}</li>
            <li>{t('legal.dataprotection.security2') || 'Access controls and authentication mechanisms'}</li>
            <li>{t('legal.dataprotection.security3') || 'Regular security audits and vulnerability assessments'}</li>
            <li>{t('legal.dataprotection.security4') || 'Employee training on data protection and security'}</li>
            <li>{t('legal.dataprotection.security5') || 'Incident response procedures for data breaches'}</li>
          </ul>

          <h3>{t('legal.dataprotection.section8') || 'Data Sharing and Disclosure'}</h3>
          <p>
            {t('legal.dataprotection.section8Text') || 'We may share your personal data with:'}
          </p>
          <ul>
            <li><strong>{t('legal.dataprotection.share1') || 'Service Providers:'}</strong> {t('legal.dataprotection.share1Text') || 'Third-party vendors who assist in providing our services'}</li>
            <li><strong>{t('legal.dataprotection.share2') || 'Regulatory Authorities:'}</strong> {t('legal.dataprotection.share2Text') || 'When required by law or to comply with legal obligations'}</li>
            <li><strong>{t('legal.dataprotection.share3') || 'Law Enforcement:'}</strong> {t('legal.dataprotection.share3Text') || 'When necessary for crime prevention or investigation'}</li>
          </ul>
          <p>
            {t('legal.dataprotection.shareNote') || 'We do not sell or rent your personal data to third parties for marketing purposes.'}
          </p>

          <h3>{t('legal.dataprotection.section9') || 'Data Retention Policy'}</h3>
          <p>
            {t('legal.dataprotection.section9Text') || 'We retain your personal data for the duration of your relationship with us and for a reasonable period thereafter to comply with legal obligations, resolve disputes, and enforce our agreements. When data is no longer needed, we securely delete or anonymize it.'}
          </p>

          <h3>{t('legal.dataprotection.section10') || "Children's Privacy"}</h3>
          <p>
            {t('legal.dataprotection.section10Text') || 'Our services are not directed at children under the age of 18. We do not knowingly collect personal data from children. If we become aware that we have collected personal data from a child, we will take steps to delete it.'}
          </p>

          <h3>{t('legal.dataprotection.section11') || 'Cross-Border Data Transfers'}</h3>
          <p>
            {t('legal.dataprotection.section11Text') || 'Some of our service providers may be located outside Kenya. When we transfer personal data across borders, we ensure that appropriate safeguards are in place to protect your data in accordance with the Kenya Data Protection Act, 2019.'}
          </p>

          <h3>{t('legal.dataprotection.section12') || 'Contact Our Data Protection Officer'}</h3>
          <p>
            {t('legal.dataprotection.section12Text') || 'If you have any questions about this Data Protection Policy or wish to exercise your data protection rights, please contact our Data Protection Officer:'}
          </p>
          <ul>
            <li><strong>{t('legal.dataprotection.contactEmail') || 'Email:'}</strong> dpo@nexcom.ke</li>
            <li><strong>{t('legal.dataprotection.contactPhone') || 'Phone:'}</strong> 0730 320 000</li>
            <li><strong>{t('legal.dataprotection.contactAddress') || 'Address:'}</strong> {t('contact.locationValue') || 'Kisumu, Kenya'}</li>
          </ul>

          <h3>{t('legal.dataprotection.section13') || 'Reporting a Data Breach'}</h3>
          <p>
            {t('legal.dataprotection.section13Text') || 'If you suspect a data breach or have concerns about your personal data, please report it immediately to our Data Protection Officer at dpo@nexcom.ke. We will investigate and take appropriate action, including notifying the Office of the Data Protection Commissioner (ODPC) if required.'}
          </p>
        </>
      )
    }
  };

  const currentLegal = legalContent[type] || legalContent.privacy;

  return (
    <Layout>
      <div className="page-container legal-page">
        <div className="page-content">
          <h1>{currentLegal.title}</h1>
          <div className="legal-content">
            {currentLegal.content}
          </div>
          <div className="legal-date">
            {t('legal.lastUpdated') || 'Last updated:'} {t('legal.lastUpdatedDate') || 'January 2025'}
          </div>
          <div className="legal-nav">
            <Link to="/legal/privacy">{t('legal.privacy.title') || 'Privacy Policy'}</Link>
            <Link to="/legal/terms">{t('legal.terms.title') || 'Terms & Conditions'}</Link>
            <Link to="/legal/cookies">{t('legal.cookies.title') || 'Cookie Policy'}</Link>
            <Link to="/legal/service">{t('legal.service.title') || 'Service Agreement'}</Link>
            <Link to="/legal/dataprotection">{t('legal.dataprotection.title') || 'Data Protection'}</Link>
          </div>
        </div>
      </div>
    </Layout>
  );
}